import { BaseProvider } from "./base.provider";

export interface Club {
  club_id: string;
  owner_user_id: string;
  club_name: string;
  club_description: string | null;
  logo_obj_path: string | null;
  country_code: string | null;
  primary_color: string;
  secondary_color: string;
  auto_accept_requests: boolean;
  member_count: number;
  created_at: string;
  score?: number;
}

export interface ClubMember {
  club_member_id: string;
  club_id: string;
  user_id: string;
  role: "owner" | "admin" | "member";
  joined_at: string;
  username: string;
  first_name: string;
  last_name: string;
  avatar_obj_path: string | null;
}

export interface ClubInvitation {
  invitation_id: string;
  club_id: string;
  club_name: string;
  user_id: string;
  initiated_by: string;
  type: "invite" | "request";
  status: "pending" | "accepted" | "declined" | "cancelled";
  created_at: string;
  updated_at: string;
  expires_at: string;
  username: string;
  first_name: string;
  last_name: string;
  avatar_obj_path: string | null;
}

export interface PaginatedClubs {
  data: Club[];
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface PaginatedMembers {
  data: ClubMember[];
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface PaginatedInvitations {
  data: ClubInvitation[];
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface UpdateClubPayload {
  club_name?: string;
  club_description?: string | null;
  country_code?: string | null;
  primary_color?: string;
  secondary_color?: string;
  auto_accept_requests?: boolean;
}

export interface RecommendedClubsParams {
  page?: number;
  country_code?: string;
  w_member_count?: number;
  w_recency?: number;
}

class ClubsProvider extends BaseProvider {
  getRecommended(params: RecommendedClubsParams = {}): Promise<PaginatedClubs> {
    const p = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) p.set(key, String(value));
    }
    return this.request<PaginatedClubs>(`/v1/clubs/recommended?${p}`);
  }


  list(page: number = 1, search?: string): Promise<PaginatedClubs> {
    const p = new URLSearchParams({ page: String(page) });
    if (search) p.set("search", search);
    return this.request<PaginatedClubs>(`/v1/clubs?${p}`);
  }

  getById(clubId: string): Promise<Club> {
    return this.request<Club>(`/v1/clubs/${encodeURIComponent(clubId)}`);
  }

  update(clubId: string, payload: UpdateClubPayload): Promise<Club> {
    return this.request<Club>(`/v1/clubs/${encodeURIComponent(clubId)}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  delete(clubId: string): Promise<string> {
    return this.request<string>(`/v1/clubs/${encodeURIComponent(clubId)}`, {
      method: "DELETE",
    });
  }

  listMembers(clubId: string, page: number = 1): Promise<PaginatedMembers> {
    return this.request<PaginatedMembers>(
      `/v1/clubs/${encodeURIComponent(clubId)}/members?page=${page}`,
    );
  }

  sendInvitation(clubId: string, userId: string): Promise<ClubInvitation> {
    return this.request<ClubInvitation>(
      `/v1/clubs/${encodeURIComponent(clubId)}/invitations`,
      { method: "POST", body: JSON.stringify({ user_id: userId }) },
    );
  }

  listInvitations(
    clubId: string,
    page: number = 1,
  ): Promise<PaginatedInvitations> {
    return this.request<PaginatedInvitations>(
      `/v1/clubs/${encodeURIComponent(clubId)}/invitations?page=${page}`,
    );
  }

  cancelInvitation(clubId: string, invId: string): Promise<string> {
    return this.request<string>(
      `/v1/clubs/${encodeURIComponent(clubId)}/invitations/${encodeURIComponent(invId)}`,
      { method: "DELETE" },
    );
  }

  listRequests(
    clubId: string,
    page: number = 1,
  ): Promise<PaginatedInvitations> {
    return this.request<PaginatedInvitations>(
      `/v1/clubs/${encodeURIComponent(clubId)}/invitations?type=request&page=${page}`,
    );
  }

  respondToRequest(
    clubId: string,
    invId: string,
    accept: boolean,
  ): Promise<string> {
    return this.request<string>(
      `/v1/clubs/${encodeURIComponent(clubId)}/requests/${encodeURIComponent(invId)}`,
      { method: "PATCH", body: JSON.stringify({ accept }) },
    );
  }

  adminList(page: number = 1, search?: string): Promise<PaginatedClubs> {
    const p = new URLSearchParams({ page: String(page) });
    if (search) p.set("search", search);
    return this.request<PaginatedClubs>(`/v1/admin/clubs?${p}`);
  }

  adminDelete(clubId: string): Promise<string> {
    return this.request<string>(
      `/v1/admin/clubs/${encodeURIComponent(clubId)}`,
      { method: "DELETE" },
    );
  }

  adminRemoveMember(clubId: string, userId: string): Promise<string> {
    return this.request<string>(
      `/v1/admin/clubs/${encodeURIComponent(clubId)}/members/${encodeURIComponent(userId)}`,
      { method: "DELETE" },
    );
  }

  adminListInvitations(
    clubId: string,
    page: number = 1,
  ): Promise<PaginatedInvitations> {
    return this.request<PaginatedInvitations>(
      `/v1/admin/clubs/${encodeURIComponent(clubId)}/invitations?page=${page}`,
    );
  }

  adminListAllInvitations(opts: {
    type?: "invite" | "request";
    status?: "pending" | "accepted" | "declined" | "cancelled";
    username?: string;
    page?: number;
  } = {}): Promise<PaginatedInvitations> {
    const p = new URLSearchParams({ page: String(opts.page ?? 1) });
    if (opts.type) p.set("type", opts.type);
    if (opts.status) p.set("status", opts.status);
    if (opts.username) p.set("username", opts.username);
    return this.request<PaginatedInvitations>(`/v1/admin/clubs/invitations?${p}`);
  }

  adminListAllMembers(page: number = 1): Promise<PaginatedMembers> {
    return this.request<PaginatedMembers>(
      `/v1/admin/clubs/members?page=${page}`,
    );
  }
}

export const clubsProvider = new ClubsProvider();
